// src/pages/Dashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import InstallPrompt from '../components/InstallPrompt';
import TodayScheduleWidget from '../components/TodayScheduleWidget';
import StarSchedule from '../components/StarSchedule';
import EventCarousel from '../components/EventCarousel';
import BottomNav from '../components/BottomNav';
import MoreButton from '../components/MoreButton';
import DesktopNav from '../components/DesktopNav';
import AppDrawer from '../components/AppDrawer';
import CampusSafety from '../components/CampusSafety';
import { useAuth } from '../context/AuthContext';
import { AlertBanner } from '../components/AlertBanner';
import './Dashboard.css';

// SVG icons as components for better performance
const StudentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
  </svg>
);

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const FoodIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
    <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
    <line x1="6" y1="1" x2="6" y2="4"></line>
    <line x1="10" y1="1" x2="10" y2="4"></line>
    <line x1="14" y1="1" x2="14" y2="4"></line>
  </svg>
);

const RefreshIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

const ShieldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

export default function Dashboard() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [campusSafetyOpen, setCampusSafetyOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, moodleData, moodleLoading, fetchMoodleData } = useAuth();

  // Refs for each section so we can scroll to them
  const topRef = useRef(null); // Reference to the top of the page
  const classesRef = useRef(null);
  const eventsRef = useRef(null); // New ref for events section

  useEffect(() => {
    // Update time every minute
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Trigger Moodle data fetch if not already loaded
  useEffect(() => {
    if (!moodleData && !moodleLoading && user) {
      fetchMoodleData();
    }
  }, [user, moodleData, moodleLoading, fetchMoodleData]);

  // Get current date in readable format
  const getCurrentDate = () => {
    const options = { weekday: 'long', month: 'long', day: 'numeric' };
    return currentTime.toLocaleDateString('en-US', options);
  };

  // Format greeting based on time of day
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Get first name from user data
  const getFirstName = () => {
    if (!user) return '';
    
    // Check for name in user object
    if (user.firstName) return user.firstName;
    if (user.displayName) {
      return user.displayName.split(' ')[0];
    }
    
    // Check Moodle data if available
    if (moodleData && moodleData.user) {
      if (moodleData.user.firstname) return moodleData.user.firstname;
      if (moodleData.user.fullname) {
        return moodleData.user.fullname.split(' ')[0];
      }
    }
    
    // Fallback to username or email
    if (user.name) return user.name;
    if (user.email) {
      return user.email.split('@')[0];
    }
    
    return 'Friend'; // Friendly fallback
  };

  // Check if we have Moodle information
  const hasMoodleData = moodleData && 
    ((moodleData.courses && moodleData.courses.length > 0) || 
     (moodleData.course_meetings && moodleData.course_meetings.length > 0));

  return (
    <div className="dashboard" ref={topRef}>
      <header className="dashboard-header">
        <button
          className="header-shield-btn"
          onClick={() => setCampusSafetyOpen(true)}
          aria-label="Campus Safety"
        >
          <ShieldIcon />
        </button>
        <div className="header-center-logo">
          <img src="/vu-connect/VITERBO_ICON_RED_RGB.svg" alt="Viterbo University" />
        </div>
        <div className="dashboard-header-right">
          <MoreButton onClick={() => setDrawerOpen(true)} />
        </div>
      </header>
      <AlertBanner />
      <CampusSafety isOpen={campusSafetyOpen} onClose={() => setCampusSafetyOpen(false)} />
      {/* Desktop navigation */}
      <DesktopNav />

      <div className="dashboard-content">
        <div className="dashboard-hero">
          <img
            src="/vu-connect/default_hero800x400.jpg"
            alt="Viterbo University Campus"
            className="hero-image"
          />
          <div className="hero-overlay">
            <h2 className="dashboard-greeting">{getGreeting()}, {getFirstName()}</h2>
            <p className="greeting-date">{getCurrentDate()}</p>
          </div>
        </div>

        <StarSchedule />

        <div className="dashboard-card" ref={classesRef}>
          <TodayScheduleWidget />
        </div>

        {/* Event Carousel Section */}
        <div className="dashboard-card" ref={eventsRef}>
          <EventCarousel />
        </div>

        <div className="dashboard-card">
          <h2><StudentIcon /> Quick Links</h2>
          <div className="quick-links">

            <Link to="/moodle" className="quick-link">
              <div className="quick-link-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                </svg>
              </div>
              <div className="quick-link-content">
                <h3>Moodle</h3>
                <p>Access course materials, assignments, and grades</p>
                <span className="external-indicator">Opens in new tab</span>
              </div>
            </Link>

            <Link to="/self-service" className="quick-link">
              <div className="quick-link-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <div className="quick-link-content">
                <h3>Self-Service</h3>
                <p>Access your student account, registration, and financial information</p>
                <span className="external-indicator">Opens in new tab</span>
              </div>
            </Link>

            <Link to="/o365" className="quick-link">
              <div className="quick-link-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <div className="quick-link-content">
                <h3>Email (O365)</h3>
                <p>Check your Viterbo email in Outlook</p>
                <span className="external-indicator">Opens in new tab</span>
              </div>
            </Link>

            <Link to="/etrieve" className="quick-link">
              <div className="quick-link-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </div>
              <div className="quick-link-content">
                <h3>Etrieve</h3>
                <p>Access and submit forms through Viterbo's document management system</p>
                <span className="external-indicator">Opens in new tab</span>
              </div>
            </Link>

            <Link to="/starfish" className="quick-link">
              <div className="quick-link-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"></path>
                </svg>
              </div>
              <div className="quick-link-content">
                <h3>Starfish</h3>
                <p>Sign up for academic support resources and testing in the HUB, formerly known as the ARC</p>
                <span className="external-indicator">Opens in new tab</span>
              </div>
            </Link>

            <Link to="/vuportal" className="quick-link">
              <div className="quick-link-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <div className="quick-link-content">
                <h3>VUPortal</h3>
                <p>Access university resources and information</p>
                <span className="external-indicator">Opens in new tab</span>
              </div>
            </Link>

            <Link to="/cafeteria" className="quick-link">
              <div className="quick-link-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
                  <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
                  <line x1="6" y1="1" x2="6" y2="4"></line>
                  <line x1="10" y1="1" x2="10" y2="4"></line>
                  <line x1="14" y1="1" x2="14" y2="4"></line>
                </svg>
              </div>
              <div className="quick-link-content">
                <h3>Cafeteria Menu</h3>
                <p>View today's dining options and meal schedules</p>
                <span className="external-indicator">Opens in new tab</span>
              </div>
            </Link>

          </div>
        </div>
      </div>

      <AppDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} onOpen={() => setDrawerOpen(true)} />
      <InstallPrompt />

      <BottomNav />
    </div>
  );
}